/* eslint-disable ember/classic-decorator-hooks, ember/no-controller-access-in-routes, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';

import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import { getSubjectData } from '@linc-technologies/linced-common/utils/reports';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { filterBy } from '@linc-technologies/linced-common/utils/arrays';

export default class PageRoute extends Route {
  // Services
  @service
  dates;

  @service
  heroApi;

  @service
  router;

  @service
  session;

  @service
  store;

  model(params) {
    let person = this.modelFor('people.person').person;
    let progressionType = ProgressionType.PROGRESSION;

    let model = {
      person,
      goalSubjects: this.store.findAll('goals/subject'),
      categories: this.store.findAll('goals/category'),
      levels: this.store.findAll('milestones/level'),
      milestones: this.store.query('milestones/milestone', { progressionType }),
      milestoneSets: this.store.query('milestones/milestone-set', {
        include: 'milestones',
      }),
      page: this.store.findRecord('posts/page', params.page_id),
      posts: this.store.query('posts/post', {
        personId: person.id,
        pageId: params.page_id,
        include: 'reactions,comments',
      }),
      subcategories: this.store.findAll('goals/subcategory'),
      grades: this.store.query('assessments/grade', { personId: person.id }),
      goals: this.store.findAll('goals/goal'),
      terms: this.store.query('term', {
        schoolId: this.session.activeSchool,
      }),
      timeframes: this.store
        .query('timeframe', {
          schoolId: this.session.activeSchool,
        })
        .then((timeframes) => {
          return timeframes
            .slice()
            .sort((a, b) =>
              a.startDateUnix > b.startDateUnix
                ? 1
                : a.startDateUnix < b.startDateUnix
                  ? -1
                  : 0,
            )
            .reverse();
        }),
    };

    let permissionParams = {
      pageId: params.page_id,
      personId: person.id,
    };
    model.pagePermissions = this.store.query(
      'posts/page-permission',
      permissionParams,
    );
    return hash(model).then((data) => {
      // Add subject-specific data to the model.
      Object.assign(
        data,
        getSubjectData(this, data.person.id, data.page.goalSubjects),
      );
      return hash(data);
    });
  }

  afterModel(model) {
    if (isEmpty(model.page.goalSubject_ids)) {
      // No subjects on this Page - no need to try fetching states.
      return;
    }

    // Fetch current states for each Subject on this Page.
    let promises = model.page.goalSubject_ids.reduce((promises, subjectId) => {
      promises.push(
        this.store.query('goals/state', {
          subjectId,
          current: true,
          personId: model.person.id,
        }),
      );
      return promises;
    }, []);

    // Merge all subjects' States, but split them into secure + developing.
    return Promise.all(promises).then((rawData) => {
      let allStates = rawData.reduce((states, subjectStates) => {
        states.push(...subjectStates.slice());
        return states;
      }, []);
      model.secureStates = filterBy(allStates, 'awarded', GoalState.SECURE);
      model.developingStates = filterBy(
        allStates,
        'awarded',
        GoalState.DEVELOPING,
      );
    });
  }

  init() {
    super.init(...arguments);

    this.router.on('routeDidChange', (transition) => {
      if (transition.to.find((route) => route.name === this.routeName)) {
        // Set activeAssessment on the controller so that it can highlight the correct button.
        let assessmentId = this.paramsFor(
          'people.person.page.assessment',
        ).assessment_id;
        this.controller.set('activeAssessment', assessmentId);
      }
    });
  }
}
